import React, { useState, useEffect, memo } from "react";
import InputMask from "react-input-mask";
import valid from "card-validator";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Validation } from "../../helpers/validations";
import { errors, spanishErrors, cardImages, states } from "../payments/constants";
import { calculateProcessingFee } from "../payments/feeCalculation";

/**
 * The fields of the emailed-payment-link form.
 *
 * Field-for-field the portal's own payment form (`pages/payments/paymentForm.jsx`
 * for card, `ECHCForm.jsx` for eCheck), including the same validators and the same
 * bilingual error strings, so a tenant who has paid through the portal sees nothing
 * unfamiliar. Two things genuinely differ:
 *
 *   - No monthly-rent hint. The pay-link context is deliberately thin (it omits the
 *     balance, because these links get forwarded and screenshotted), so the only
 *     figure available to pre-fill is the amount Ania quoted on the call.
 *   - Card and eCheck are tabs on one page rather than two separate modals.
 *
 * There is deliberately NO email field. The receipt goes to the address the link was
 * emailed to, which the backend reads from the person record — the browser is never
 * asked for it and could not change it if it tried.
 *
 * The cardholder name IS asked for, pre-filled from the person record. It has to
 * stay editable: the gateway verifies that name against the card, and a tenant's
 * relative paying on their own card is normal and must not be rejected.
 *
 * The container owns submission and the decline/retry cycle. This component owns
 * only the fields, their validation, and the review step.
 */

const sectionLabelStyle = {
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "#6c757d",
  marginBottom: "8px",
  marginTop: "4px",
};

const sectionDividerStyle = {
  borderTop: "1px solid #d5dbe0",
  margin: "14px 0 10px",
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  padding: "7px 0",
  fontSize: "13px",
  lineHeight: "1.5",
};

const labelStyle = { color: "#6c757d" };

const CARD = "Credit Card";
const ECHECK = "eCheck";

const PayByLinkForm = memo(
  ({
    suggestedAmount,
    suggestedFirstName,
    suggestedLastName,
    feeRatePercent,
    submitting,
    /**
     * Bumped by the container after every decline. Clears the card / bank numbers
     * so the tenant can enter a different instrument, while leaving the amount and
     * billing address alone — retyping all of that after a decline is the single
     * most annoying thing this page could do.
     */
     secretsResetSignal,
    onSubmit,
  }) => {
    const { t } = useTranslation();

    const [method, setMethod] = useState(CARD);
    const [amount, setAmount] = useState(suggestedAmount || "");

    const [cardNumber, setCardNumber] = useState("");
    const [cardCode, setCardCode] = useState("");
    const [expirationDate, setExpiration] = useState("");
    const [showCardNumber, setShowCardNumber] = useState(false);
    const [cardImage, setCardImage] = useState(cardImages["default"]);

    const [checkAccount, setCheckAccount] = useState("");
    const [confirmCheckAccount, setConfirmCheckAccount] = useState("");
    const [checkAba, setCheckAba] = useState("");
    const [showCheckAccount, setShowCheckAccount] = useState(false);
    const [showConfirmAccount, setShowConfirmAccount] = useState(false);

    const [error, setError] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);

    const [address, setAddress] = useState({
      firstname: suggestedFirstName || "",
      lastname: suggestedLastName || "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
    });

    const feeInfo = calculateProcessingFee(amount);
    const lang = localStorage.getItem("i18nextLng") === "es";
    const msg = (key) => (lang ? spanishErrors[key] : errors[key]);

    /**
     * The fee is computed in three places — here, in the backend's `fees.ts`, and
     * in the portal's `capturePayment`. They are identical today and the backend is
     * authoritative, but if they ever drift the tenant would be shown one total and
     * charged another. The context endpoint reports the rate the server will use,
     * so a mismatch is caught here rather than discovered on a statement.
     */
    const feeRateMismatch =
      feeInfo &&
      typeof feeRatePercent === "number" &&
      Math.abs(feeRatePercent - feeInfo.feePercent) > 0.001;

    // Clear only the sensitive instrument fields after a decline.
    useEffect(() => {
      if (!secretsResetSignal) return;
      setCardNumber("");
      setCardCode("");
      setExpiration("");
      setCardImage(cardImages["default"]);
      setCheckAccount("");
      setConfirmCheckAccount("");
      setCheckAba("");
      setShowConfirmation(false);
      setError({});
    }, [secretsResetSignal]);

    const clearError = (field) => setError((prev) => ({ ...prev, [field]: "" }));

    const handleValidation = (type) => {
      if (type === "amount") {
        if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) < 1) {
          // Floor of 1.00, matching capturePaymentLinkSchema on the backend. The
          // portal says 0.01; rejecting here rather than after a round-trip.
          setError((prev) => ({ ...prev, amount: `${msg("amount")}1.00` }));
          return false;
        }
      } else if (type === "cardNumber") {
        if (!valid.number(cardNumber, { luhnValidateUnionPay: true }).isValid) {
          setError((prev) => ({ ...prev, cardNumber: msg("cardNumber") }));
          return false;
        }
      } else if (type === "expirationDate") {
        const expireValidation = valid.expirationDate(expirationDate);
        if (!expireValidation.isPotentiallyValid || !expireValidation.isValid) {
          let text = msg("expirationDate");
          if (moment(expirationDate, "MM/YYYY").diff(moment.now()) < 0) {
            text = msg("cardExpired");
          }
          setError((prev) => ({ ...prev, expirationDate: text }));
          return false;
        }
      } else if (type === "cardCode") {
        if (cardCode.length < 3 || cardCode.length > 4) {
          setError((prev) => ({ ...prev, cardCode: msg("cardCode") }));
          return false;
        }
      } else if (type === "checkAccount") {
        if (!checkAccount || checkAccount.length < 8) {
          setError((prev) => ({ ...prev, checkAccount: msg("checkAccount") }));
          return false;
        }
      } else if (type === "confirmCheckAccount") {
        if (!confirmCheckAccount || confirmCheckAccount.length < 8) {
          setError((prev) => ({ ...prev, confirmCheckAccount: msg("checkAccount") }));
          return false;
        }
        if (checkAccount !== confirmCheckAccount) {
          setError((prev) => ({
            ...prev,
            confirmCheckAccount: msg("confirmCheckAccount"),
          }));
          return false;
        }
      } else if (type === "checkAba") {
        if (!checkAba || checkAba.length !== 9) {
          setError((prev) => ({ ...prev, checkAba: msg("checkAba") }));
          return false;
        }
      } else if (type === "firstname") {
        if (Validation.empty(address.firstname)) {
          setError((prev) => ({ ...prev, firstname: msg("name") }));
          return false;
        }
      } else if (type === "lastname") {
        if (Validation.empty(address.lastname)) {
          setError((prev) => ({ ...prev, lastname: msg("name") }));
          return false;
        }
      } else if (type === "addressLine1") {
        if (Validation.empty(address.addressLine1)) {
          setError((prev) => ({ ...prev, addressLine1: msg("addressLine1") }));
          return false;
        }
      } else if (type === "city") {
        if (Validation.empty(address.city)) {
          setError((prev) => ({ ...prev, city: msg("city") }));
          return false;
        }
      } else if (type === "state") {
        if (Validation.empty(address.state)) {
          setError((prev) => ({ ...prev, state: msg("state") }));
          return false;
        }
      } else if (type === "postalCode") {
        if (!Validation.postalCode(address.postalCode)) {
          setError((prev) => ({ ...prev, postalCode: msg("postalCode") }));
          return false;
        }
      }
      return true;
    };

    const instrumentFields =
      method === CARD
        ? ["cardNumber", "expirationDate", "cardCode"]
        : ["checkAccount", "confirmCheckAccount", "checkAba"];

    const handleReview = (e) => {
      e.preventDefault();
      const allFields = [
        "amount",
        ...instrumentFields,
        "firstname",
        "lastname",
        "addressLine1",
        "city",
        "state",
        "postalCode",
      ];
      for (const field of allFields) {
        if (!handleValidation(field)) return;
      }
      setShowConfirmation(true);
    };

    const handleConfirm = () => {
      const base = {
        amount: parseFloat(amount).toFixed(2),
        // No email: the backend uses the address it sent the link to.
        first_name: address.firstname,
        last_name: address.lastname,
        address1: address.addressLine1,
        address2: address.addressLine2,
        city: address.city,
        state: address.state,
        zip: address.postalCode,
        country: "US",
        type: method,
      };

      if (method === CARD) {
        const parts = expirationDate.split("/");
        // NMI wants MMYY; the field collects MM/YYYY.
        const formattedExp =
          parts.length === 2 ? `${parts[0]}${parts[1].slice(-2)}` : expirationDate;
        onSubmit({
          ...base,
          ccnumber: cardNumber.toString(),
          ccexp: formattedExp,
          cvv: cardCode.toString(),
          type_more: valid.number(cardNumber)?.card?.type,
        });
        return;
      }

      onSubmit({
        ...base,
        checkaccount: checkAccount,
        checkaba: checkAba,
      });
    };

    const handleCardNumberChange = (value) => {
      const numberValidation = valid.number(value);
      setCardNumber(value);
      setCardImage(
        numberValidation.isPotentiallyValid && numberValidation.card
          ? cardImages[numberValidation.card.type] || cardImages["default"]
          : cardImages["default"]
      );
    };

    const handleAmountChange = (value) => {
      const clean = value.replace(/[^0-9.]/g, "");
      const dotIdx = clean.indexOf(".");
      if (dotIdx !== -1) {
        setAmount(
          clean.substring(0, dotIdx) +
            "." +
            clean.substring(dotIdx + 1).replace(/\./g, "").slice(0, 2)
        );
      } else {
        setAmount(clean);
      }
    };

    const handleAddressChange = (key, value) =>
      setAddress((prev) => ({ ...prev, [key]: value }));

    const maskValue = (val) => "*".repeat(val.length);

    const cardBrand = valid.number(cardNumber)?.card?.niceType || "Card";
    const maskedCard = cardNumber.length >= 4 ? `**** ${cardNumber.slice(-4)}` : "";
    const maskedAccount =
      checkAccount.length >= 4 ? `**** ${checkAccount.slice(-4)}` : "";
    const addressLine = [address.addressLine1, address.addressLine2]
      .filter(Boolean)
      .join(", ");
    const cityStateZip = [address.city, address.state, address.postalCode]
      .filter(Boolean)
      .join(", ");

    const feeSummary = (
      <>
        <div style={rowStyle}>
          <span style={labelStyle}>{t("modal.payment.payment-amount", "Rent Amount")}</span>
          <span>${feeInfo ? feeInfo.baseAmount.toFixed(2) : "0.00"}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>{t("modal.payment.processing-fee", "Processing Fee")}</span>
          <span>${feeInfo ? feeInfo.feeAmount.toFixed(2) : "0.00"}</span>
        </div>
        <div
          style={{
            borderTop: "1px solid #d5dbe0",
            marginTop: "4px",
            paddingTop: "8px",
            display: "flex",
            justifyContent: "space-between",
            fontSize: "15px",
          }}
        >
          <strong>{t("modal.payment.total-charge", "Total Charge")}</strong>
          <strong>${feeInfo ? feeInfo.totalCharge.toFixed(2) : "0.00"}</strong>
        </div>
      </>
    );

    if (showConfirmation) {
      return (
        <section className="paymentform-container">
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <i
              className="fa fa-shield-alt"
              style={{ fontSize: "28px", color: "#348fe2", marginBottom: "6px" }}
            />
            <h5 style={{ fontWeight: 600, margin: "4px 0 0" }}>
              {t("modal.payment.review-title", "Review Your Payment")}
            </h5>
          </div>

          <div style={sectionLabelStyle}>
            {method === CARD
              ? t("modal.payment.card-details", "Card Information")
              : t("modal.payment.echeck-details", "Bank Account")}
          </div>
          {method === CARD ? (
            <>
              <div style={rowStyle}>
                <span style={labelStyle}>{t("modal.payment.card", "Card")}</span>
                <span>
                  {cardBrand} {maskedCard}
                </span>
              </div>
              <div style={rowStyle}>
                <span style={labelStyle}>{t("modal.payment.expires", "Expires")}</span>
                <span>{expirationDate}</span>
              </div>
            </>
          ) : (
            <>
              <div style={rowStyle}>
                <span style={labelStyle}>{t("modal.payment.account", "Account")}</span>
                <span>{maskedAccount}</span>
              </div>
              <div style={rowStyle}>
                <span style={labelStyle}>{t("modal.payment.routing", "Routing")}</span>
                <span>{checkAba}</span>
              </div>
            </>
          )}

          <div style={{ ...sectionDividerStyle, marginTop: "6px" }} />
          <div style={sectionLabelStyle}>
            {t("modal.payment.billing-address", "Billing Address")}
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>{t("modal.payment.name", "Name")}</span>
            <span>
              {address.firstname} {address.lastname}
            </span>
          </div>
          {addressLine && (
            <div style={rowStyle}>
              <span style={labelStyle}>{t("modal.payment.address", "Address")}</span>
              <span style={{ textAlign: "right" }}>{addressLine}</span>
            </div>
          )}
          {cityStateZip && (
            <div style={rowStyle}>
              <span style={labelStyle} />
              <span style={{ textAlign: "right" }}>{cityStateZip}</span>
            </div>
          )}
          {/*
            The actual address is not shown. It is the one this link was emailed to,
            and the context endpoint deliberately does not return it — these links
            get forwarded and screenshotted, so there is no reason to put a tenant's
            email in a response that anyone holding the link can read.
          */}
          <div style={rowStyle}>
            <span style={labelStyle}>{t("modal.payment.receipt-to", "Receipt to")}</span>
            <span style={{ textAlign: "right" }}>
              {t("paylink.receipt-to-on-file", "The email address on your account")}
            </span>
          </div>

          <div style={{ ...sectionDividerStyle, marginTop: "6px" }} />
          <div style={sectionLabelStyle}>
            {t("modal.payment.payment-summary", "Payment Summary")}
          </div>
          {feeSummary}

          <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
            <button
              className="btn btn-secondary"
              style={{ flex: "0 0 100px", padding: "10px", fontSize: "14px" }}
              disabled={submitting}
              onClick={() => setShowConfirmation(false)}
            >
              {t("modal.payment.back", "Back")}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary"
              style={{ flex: 1, padding: "10px", fontSize: "14px", fontWeight: 600 }}
              onClick={handleConfirm}
            >
              {!submitting ? (
                feeInfo ? `Pay $${feeInfo.totalCharge.toFixed(2)}` : "Pay"
              ) : (
                <i className="fas fa-spinner fa-pulse" />
              )}
            </button>
          </div>
        </section>
      );
    }

    return (
      <section className="paymentform-container">
        {/* --- Amount --- */}
        <div style={sectionLabelStyle}>
          {t("modal.payment.enter-amount", "Rent Amount")}
        </div>
        {suggestedAmount && (
          <p style={{ fontSize: "12px", color: "#6c757d", margin: "0 0 6px" }}>
            {t("paylink.amount-hint", "Amount discussed on your call")}:{" "}
            <strong>${parseFloat(suggestedAmount).toFixed(2)}</strong>{" "}
            {t("paylink.amount-editable", "— you can change this")}
          </p>
        )}
        <div className="form-group m-b-10">
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text">
                <i className="fa fa-dollar-sign" />
              </span>
            </div>
            <input
              type="text"
              inputMode="decimal"
              className={`form-control form-control-lg ${error.amount ? "is-invalid" : ""}`}
              placeholder="0.00"
              value={amount}
              onChange={(e) => handleAmountChange(e.target.value)}
              onBlur={() => handleValidation("amount")}
              onFocus={() => clearError("amount")}
            />
          </div>
          {error.amount && <div className="invalid-feedback d-block">{error.amount}</div>}
        </div>

        {/* --- Method --- */}
        <div style={sectionDividerStyle} />
        <ul className="nav nav-pills nav-justified m-b-10">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link btn btn-link ${method === CARD ? "active" : ""}`}
              onClick={() => {
                setMethod(CARD);
                setError({});
              }}
            >
              <i className="fa fa-credit-card m-r-5" />
              {t("modal.payment.card-tab", "Card")}
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link btn btn-link ${method === ECHECK ? "active" : ""}`}
              onClick={() => {
                setMethod(ECHECK);
                setError({});
              }}
            >
              <i className="fa fa-university m-r-5" />
              {t("modal.payment.echeck-tab", "Bank Account")}
            </button>
          </li>
        </ul>

        {method === CARD ? (
          <>
            <div style={sectionLabelStyle}>
              {t("modal.payment.card-details", "Card Information")}
            </div>
            <div className="form-group m-b-10">
              <div className="input-group">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  className={`form-control form-control-lg ${error.cardNumber ? "is-invalid" : ""}`}
                  placeholder={t("modal.payment.cardno", "Card number")}
                  value={showCardNumber ? cardNumber : maskValue(cardNumber)}
                  onChange={(e) =>
                    handleCardNumberChange(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  onBlur={() => {
                    handleValidation("cardNumber");
                    setShowCardNumber(false);
                  }}
                  onFocus={() => {
                    clearError("cardNumber");
                    setShowCardNumber(true);
                  }}
                />
                <div className="input-group-append">
                  <span className="input-group-text" style={{ background: "#fff" }}>
                    <i className={cardImage} style={{ fontSize: "1.4em" }} />
                  </span>
                </div>
              </div>
              {error.cardNumber && (
                <div className="invalid-feedback d-block">{error.cardNumber}</div>
              )}
            </div>

            <div className="row">
              <div className="col-7 pr-1">
                <div className="form-group m-b-10">
                  <InputMask
                    mask="99/9999"
                    className={`form-control form-control-lg ${error.expirationDate ? "is-invalid" : ""}`}
                    value={expirationDate}
                    placeholder="MM/YYYY"
                    onChange={(e) => setExpiration(e.target.value)}
                    onBlur={() => handleValidation("expirationDate")}
                    onFocus={() => clearError("expirationDate")}
                  />
                  {error.expirationDate && (
                    <div className="invalid-feedback d-block">{error.expirationDate}</div>
                  )}
                </div>
              </div>
              <div className="col-5 pl-1">
                <div className="form-group m-b-10">
                  <input
                    type="password"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    className={`form-control form-control-lg ${error.cardCode ? "is-invalid" : ""}`}
                    placeholder={t("modal.payment.cardcode", "CVV")}
                    value={cardCode}
                    onChange={(e) =>
                      setCardCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))
                    }
                    onBlur={() => handleValidation("cardCode")}
                    onFocus={() => clearError("cardCode")}
                  />
                  {error.cardCode && (
                    <div className="invalid-feedback d-block">{error.cardCode}</div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={sectionLabelStyle}>
              {t("modal.payment.echeck-details", "Bank Account")}
            </div>
            <div className="form-group m-b-10">
              <div className="input-group">
                <input
                  type="text"
                  inputMode="numeric"
                  className={`form-control form-control-lg ${error.checkAccount ? "is-invalid" : ""}`}
                  placeholder={t("modal.payment.account-no", "Account number")}
                  value={showCheckAccount ? checkAccount : maskValue(checkAccount)}
                  onChange={(e) =>
                    setCheckAccount(e.target.value.replace(/[^0-9]/g, "").slice(0, 17))
                  }
                  onBlur={() => {
                    handleValidation("checkAccount");
                    setShowCheckAccount(false);
                  }}
                  onFocus={() => {
                    clearError("checkAccount");
                    setShowCheckAccount(true);
                  }}
                />
              </div>
              {error.checkAccount && (
                <div className="invalid-feedback d-block">{error.checkAccount}</div>
              )}
            </div>

            <div className="form-group m-b-10">
              <input
                type="text"
                inputMode="numeric"
                className={`form-control form-control-lg ${error.confirmCheckAccount ? "is-invalid" : ""}`}
                placeholder={t("modal.payment.confirm-account-no", "Confirm account number")}
                value={showConfirmAccount ? confirmCheckAccount : maskValue(confirmCheckAccount)}
                onChange={(e) =>
                  setConfirmCheckAccount(e.target.value.replace(/[^0-9]/g, "").slice(0, 17))
                }
                onBlur={() => {
                  handleValidation("confirmCheckAccount");
                  setShowConfirmAccount(false);
                }}
                onFocus={() => {
                  clearError("confirmCheckAccount");
                  setShowConfirmAccount(true);
                }}
              />
              {error.confirmCheckAccount && (
                <div className="invalid-feedback d-block">{error.confirmCheckAccount}</div>
              )}
            </div>

            <div className="form-group m-b-10">
              <input
                type="text"
                inputMode="numeric"
                className={`form-control form-control-lg ${error.checkAba ? "is-invalid" : ""}`}
                placeholder={t("modal.payment.routing-no", "Routing number (9 digits)")}
                value={checkAba}
                onChange={(e) =>
                  setCheckAba(e.target.value.replace(/[^0-9]/g, "").slice(0, 9))
                }
                onBlur={() => handleValidation("checkAba")}
                onFocus={() => clearError("checkAba")}
              />
              {error.checkAba && (
                <div className="invalid-feedback d-block">{error.checkAba}</div>
              )}
            </div>
          </>
        )}

        {/* --- Billing address --- */}
        <div style={sectionDividerStyle} />
        <div style={sectionLabelStyle}>
          {t("modal.payment.billing-address", "Billing Address")}
        </div>

        <div className="row">
          <div className="col-6 pr-1">
            <div className="form-group m-b-10">
              <input
                type="text"
                className={`form-control form-control-lg ${error.firstname ? "is-invalid" : ""}`}
                placeholder={t("modal.payment.firstname", "First name")}
                value={address.firstname}
                onChange={(e) => handleAddressChange("firstname", e.target.value)}
                onBlur={() => handleValidation("firstname")}
                onFocus={() => clearError("firstname")}
              />
              {error.firstname && (
                <div className="invalid-feedback d-block">{error.firstname}</div>
              )}
            </div>
          </div>
          <div className="col-6 pl-1">
            <div className="form-group m-b-10">
              <input
                type="text"
                className={`form-control form-control-lg ${error.lastname ? "is-invalid" : ""}`}
                placeholder={t("modal.payment.lastname", "Last name")}
                value={address.lastname}
                onChange={(e) => handleAddressChange("lastname", e.target.value)}
                onBlur={() => handleValidation("lastname")}
                onFocus={() => clearError("lastname")}
              />
              {error.lastname && (
                <div className="invalid-feedback d-block">{error.lastname}</div>
              )}
            </div>
          </div>
        </div>

        <div className="form-group m-b-10">
          <input
            type="text"
            className={`form-control form-control-lg ${error.addressLine1 ? "is-invalid" : ""}`}
            placeholder={t("modal.payment.addL1", "Address line 1")}
            value={address.addressLine1}
            onChange={(e) => handleAddressChange("addressLine1", e.target.value)}
            onBlur={() => handleValidation("addressLine1")}
            onFocus={() => clearError("addressLine1")}
          />
          {error.addressLine1 && (
            <div className="invalid-feedback d-block">{error.addressLine1}</div>
          )}
        </div>

        <div className="form-group m-b-10">
          <input
            type="text"
            className="form-control form-control-lg"
            placeholder={t("modal.payment.addL2", "Address line 2 (optional)")}
            value={address.addressLine2}
            onChange={(e) => handleAddressChange("addressLine2", e.target.value)}
          />
        </div>

        <div className="row">
          <div className="col-5 pr-1">
            <div className="form-group m-b-10">
              <input
                type="text"
                className={`form-control form-control-lg ${error.city ? "is-invalid" : ""}`}
                placeholder={t("modal.payment.city", "City")}
                value={address.city}
                onChange={(e) => handleAddressChange("city", e.target.value)}
                onBlur={() => handleValidation("city")}
                onFocus={() => clearError("city")}
              />
              {error.city && <div className="invalid-feedback d-block">{error.city}</div>}
            </div>
          </div>
          <div className="col-4 px-1">
            <div className="form-group m-b-10">
              <select
                className={`form-control form-control-lg ${error.state ? "border border-danger" : ""}`}
                value={address.state}
                onChange={(e) => handleAddressChange("state", e.target.value)}
                onBlur={() => handleValidation("state")}
                onFocus={() => clearError("state")}
              >
                <option value="">{t("modal.payment.select-state", "State")}</option>
                {states.map((item, i) => (
                  <option key={i} value={item.State}>
                    {item.Code}
                  </option>
                ))}
              </select>
              {error.state && <div className="invalid-feedback d-block">{error.state}</div>}
            </div>
          </div>
          <div className="col-3 pl-1">
            <div className="form-group m-b-10">
              <input
                type="text"
                inputMode="numeric"
                className={`form-control form-control-lg ${error.postalCode ? "is-invalid" : ""}`}
                placeholder={t("modal.payment.postal", "ZIP")}
                value={address.postalCode}
                onChange={(e) =>
                  handleAddressChange("postalCode", e.target.value.replace(/[^0-9\-]/g, ""))
                }
                onBlur={() => handleValidation("postalCode")}
                onFocus={() => clearError("postalCode")}
              />
              {error.postalCode && (
                <div className="invalid-feedback d-block">{error.postalCode}</div>
              )}
            </div>
          </div>
        </div>

        {/* --- Fee summary --- */}
        {feeInfo && (
          <>
            <div style={sectionDividerStyle} />
            {feeRateMismatch && (
              <div className="alert alert-warning" style={{ fontSize: "12px" }}>
                {t(
                  "paylink.fee-mismatch",
                  "We could not confirm the processing fee. Please call our office before paying."
                )}
              </div>
            )}
            <div style={{ fontSize: "13px", marginBottom: "12px" }}>{feeSummary}</div>
          </>
        )}

        <button
          type="submit"
          disabled={submitting || feeRateMismatch}
          className="btn btn-warning btn-block"
          style={{ padding: "10px", fontSize: "15px", fontWeight: 600 }}
          onClick={handleReview}
        >
          {feeInfo
            ? `${t("modal.payment.review-pay", "Review & Pay")} $${feeInfo.totalCharge.toFixed(2)}`
            : t("modal.payment.review-pay", "Review & Pay")}
        </button>
      </section>
    );
  }
);

export default PayByLinkForm;
